import { useState, useEffect, useCallback } from 'react';

import { Form } from '@unform/web';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ImageUserPictureExample } from '~/assets/images';
import {
  Button,
  GoBackButton,
  ConfirmModal,
  Switch,
  Select,
} from '~/components';
import ContractModal from '~/components/ContractModal';
import { useAuth } from '~/hooks/auth.context';
import { useLoading } from '~/hooks/loading';
import { IPagination } from '~/models/Common';
import { IUserContract } from '~/models/Contracts';
import { IUser } from '~/models/User';
import { ProfessionalService } from '~/services';
import ContractsService from '~/services/Contracts';
import formatDateISO from '~/utils/formatDateIso';
import { applyMask, formatUTCDateToBR } from '~/utils/masks';
import parseDateFromISO from '~/utils/parseDateFromISO';
import { statusOptions } from '~/utils/status';

import * as S from './styles';

interface IUserDetailsProps {
  id: string;
}

const contractsOptions = [
  { label: '#001', value: '001' },
  { label: '#002', value: '002' },
  { label: '#003', value: '003' },
  { label: '#004', value: '004' },
];

const UserDetails = () => {
  const { setLoading } = useLoading();

  const { authToken } = useAuth();
  const [user, setUser] = useState<IUser>();
  const [contracts, setContracts] = useState<IUserContract[]>([]);

  const [currentContract, setCurrentContract] = useState<IUserContract | null>(
    null,
  );
  const [contractsValues, setContractsValues] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [selectedContract, setSelectedContract] = useState<string | null>(null);
  const [blockUser, setBlockUser] = useState<boolean | undefined>(
    user?.professional?.status,
  );
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [students, setStudents] = useState<IPagination<IUser[]>>();
  const { id } = useParams<IUserDetailsProps>();

  const [contractId, setContractId] = useState<number>(-1);
  const [contractModal, setContractModal] = useState(false);
  const [contractEditBlockedModal, setContractEditBlockedModal] = useState(
    false,
  );

  const router = useHistory();

  /* const handleNavigateToPackageDetail = useCallback(
    (packageId: string, packageData: IPackage) => {
      router.push(`/users/details/${id}/packages/${packageId}`, {
        packageData,
      });
    },
    [id, router],
  ); */

  // Editar treinador
  /* const handleNavigateToEditUser = useCallback(() => {
    router.push(`/users/edit/${id}`);
  }, [id, router]); */

  // Visualizar os pacotes do treinador
  /* const handleNavigateToPackages = useCallback(() => {
    router.push(`/users/details/${id}/packages`);
  }, [id, router]); */

  const handleGetUser = useCallback(async () => {
    try {
      setLoading(true);

      const [fetchedUser] = await Promise.all([
        ProfessionalService.getOneUser(id),
      ]);
      setUser(fetchedUser.data);
      setBlockUser(fetchedUser.data.professional?.status);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao carregar os dados!');
    }
  }, [id, setLoading]);

  const handleGetStudents = useCallback(async () => {
    if (!user?.professional?.id) return;
    try {
      setLoading(true);

      const [fetchedUser] = await Promise.all([
        ProfessionalService.getProfessionalStudents(
          user?.professional?.id,
          1,
          250,
        ),
      ]);

      setStudents(fetchedUser.data);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Profissional com fatura não paga ou cancelada');
    }
  }, [id, user?.professional?.id]);

  const handleGetContracts = useCallback(async () => {
    if (!user?.professional?.id) return;
    try {
      setLoading(true);

      const [fetchedContract] = await Promise.all([
        ContractsService.getAllContracts(String(user?.professional?.id)),
      ]);
      if (fetchedContract && fetchedContract.data.length !== 0) {
        setContracts(fetchedContract.data);
        const contractsOptions = fetchedContract.data.map((contractItem) => ({
          label: String(contractItem.contract_number),
          value: String(contractItem.id),
        }));

        setContractsValues(contractsOptions);
        setSelectedContract(contractsOptions[0].value);
      }
    } catch (error) {
      console.log(error);
      toast.error('Erro ao buscar contratos.');
    } finally {
      setLoading(false);
    }
  }, [setLoading, user?.professional?.id]);

  const handleGetOneContract = useCallback(async () => {
    if (!selectedContract) return;
    try {
      setLoading(true);

      const [fetchedContract] = await Promise.all([
        ContractsService.getOneContract(selectedContract),
      ]);
      if (fetchedContract) {
        setCurrentContract(fetchedContract.data[0]);
      }
    } catch (error) {
      toast.error('Erro ao buscar contratos.');
    } finally {
      setLoading(false);
    }
  }, [selectedContract, setLoading]);

  const handleUpdateProfessionalStatus = useCallback(async () => {
    try {
      setLoading(true);

      if (!user?.professional?.id) return;

      const [fetchedUser] = await Promise.all([
        ProfessionalService.updateUser(id, user.professional.id, !blockUser),
      ]);

      if (fetchedUser) {
        setBlockUser((prevState) => !prevState);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao carregar os dados!');
    }
  }, [blockUser, id, user?.professional?.id]);

  const handleDeleteProfessional = useCallback(async () => {
    try {
      setLoading(true);

      const [fetchedUser] = await Promise.all([
        ProfessionalService.deleteUser(id),
      ]);
      if (fetchedUser) {
        setConfirmDeleteModal(false);
        toast.success('Usuário excluído com sucesso!');
        router.push('/users');
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao deletar o usuario!');
    }
  }, [id, router]);

  const handleChangeStatus = useCallback(
    async (newStatus: string) => {
      // if (
      //   currentContract?.status === 'finalized' ||
      //   currentContract?.status === 'cancelled'
      // ) {
      //   toast.error(
      //     'Não é possível alterar o Status se ele estiver finalizado ou cancelado.',
      //   );
      //   return;
      // }
      try {
        setLoading(true);
        const response = await ContractsService.editContract(
          String(currentContract?.id),
          {
            activation_date: currentContract?.activation_date
              ? currentContract?.activation_date
              : '',
            period_contract_month: currentContract?.period_contract_month
              ? String(currentContract?.period_contract_month)
              : '',
            period_contract_initial: currentContract?.period_contract_initial
              ? String(currentContract?.period_contract_initial)
              : '',
            period_contract_final: currentContract?.period_contract_final
              ? String(currentContract?.period_contract_final)
              : '',
            contract_value: currentContract?.contract_value
              ? currentContract?.contract_value
              : '',
            payment_modality: currentContract?.payment_modality
              ? currentContract?.payment_modality
              : '',
            quantity_installment: currentContract?.quantity_installment
              ? String(currentContract?.quantity_installment)
              : '',
            installment_value: currentContract?.installment_value
              ? String(currentContract?.installment_value)
              : '',
            payment_method: currentContract?.payment_method
              ? String(currentContract?.payment_method)
              : '',
            status: newStatus,
          },
        );
        if (response) {
          toast.success('Status alterado com sucesso!');
          await handleGetOneContract();
        }
      } catch (err) {
        if (axios.isAxiosError(err)) {
          toast.error(err.response?.data.message);
        } else {
          toast.error('Ocorreu um erro');
        }
      } finally {
        setLoading(false);
      }
    },
    [
      currentContract?.id,
      currentContract?.activation_date,
      currentContract?.period_contract_month,
      currentContract?.period_contract_initial,
      currentContract?.period_contract_final,
      currentContract?.contract_value,
      currentContract?.payment_modality,
      currentContract?.quantity_installment,
      currentContract?.installment_value,
      currentContract?.payment_method,
      setLoading,
      handleGetOneContract,
    ],
  );

  useEffect(() => {
    handleGetUser();
  }, [handleGetUser]);

  useEffect(() => {
    handleGetContracts();
  }, [handleGetContracts, contractModal]);

  useEffect(() => {
    handleGetOneContract();
  }, [handleGetOneContract, selectedContract]);

  useEffect(() => {
    handleGetStudents();
  }, [handleGetStudents, user]);

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.Content>
          <S.Header>
            <S.UserAvatar
              src={user?.image_url ?? ImageUserPictureExample}
              alt="Perfil"
            />

            <section>
              <S.Title>{user?.name}</S.Title>

              <div>
                <S.InfosItemTitle>Cadastro: </S.InfosItemTitle>

                <h3 className="item-value">
                  {' '}
                  {user?.professional?.created_at
                    ?.split(' ')[0]
                    .split('-')
                    .reverse()
                    .join()
                    .replaceAll(',', '/')}
                </h3>
              </div>
            </section>
          </S.Header>
          <S.InfosContainer>
            <S.InfosItem>
              <S.InfosItemTitle>Nome público</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.professional?.public_name}
                {user?.professional?.id}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Nome</S.InfosItemTitle>
              <S.InfosItemValue>{user?.name}</S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>
                {user?.professional?.cpf.length === 11 ? 'CPF' : 'CNPJ'}
              </S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.professional?.cpf.length === 11
                  ? applyMask({
                      name: 'cpf',
                      value: user?.professional?.cpf || '',
                    })
                  : applyMask({
                      name: 'cnpj',
                      value: user?.professional?.cpf || '',
                    })}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Registro Profissional</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.professional?.cref ? user?.professional?.cref : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Data de nascimento</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.professional?.birth_date
                  ? formatUTCDateToBR(user?.professional.birth_date)
                  : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>E-mail</S.InfosItemTitle>
              <S.InfosItemValue href={`mailto:${user?.email}`}>
                {user?.email}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Celular</S.InfosItemTitle>
              <S.InfosItemValue
                href={`https://api.whatsapp.com/send?phone=${user?.cellphone}`}
              >
                {applyMask({
                  name: 'telefone',
                  value: user?.cellphone || '',
                })}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>CEP</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_zip_code
                  ? applyMask({
                      name: 'cep',
                      value: user?.address_zip_code,
                    })
                  : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Endereço</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_street ? user?.address_street : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Número</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_number ? user?.address_number : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Complemento</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_complement ? user?.address_complement : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Bairro</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_neighborhood
                  ? user?.address_neighborhood
                  : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Cidade</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_city ? user?.address_city : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Estado</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_state ? user?.address_state : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>País</S.InfosItemTitle>
              <S.InfosItemValue>
                {user?.address_country ? user?.address_country : '----'}
              </S.InfosItemValue>
            </S.InfosItem>
          </S.InfosContainer>
          <S.Footer>
            {/* <Button
              variant="black"
              textContent="Editar"
              onClick={handleNavigateToEditUser}
              size="small"
              extraStyles={{
                maxWidth: '20rem',
                maxHeight: '3.5rem',
              }}
            /> */}
            <Button
              variant="yellow"
              onClick={handleUpdateProfessionalStatus}
              size="small"
            >
              <Switch name="undefined" disabled checked={!blockUser} />
              {blockUser ? 'Bloquear usuário' : 'Desbloquear usuário'}
            </Button>
            <Button
              variant="red"
              textContent="Excluir usuário"
              onClick={() => setConfirmDeleteModal(true)}
              size="small"
            />
          </S.Footer>
        </S.Content>

        <S.Aside>
          {/* user?.professional?.contracts */}

          <S.AsideCard>
            <S.AsideCardTitle style={{ width: '100%', textAlign: 'left' }}>
              Contratos
            </S.AsideCardTitle>
            {contracts && contracts.length > 0 && currentContract && (
              <>
                <S.AsideCardInsideContainer containerType="column">
                  <div />
                  <S.AsideCardItem>
                    <Form
                      onSubmit={() => {
                        console.log('contracts');
                      }}
                      style={{ width: '60%' }}
                    >
                      <Select
                        style={{ textAlign: 'left', padding: '0 8px' }}
                        containerStyle={{ marginBottom: '0' }}
                        name="contracts"
                        value={selectedContract ? String(selectedContract) : ''}
                        options={contractsValues}
                        onChange={(event) => {
                          setSelectedContract(event.target.value);
                        }}
                      />
                    </Form>
                  </S.AsideCardItem>
                  <S.AsideCardItem>
                    Data de ativação:
                    <S.AsideCardItemValue>
                      {parseDateFromISO(
                        currentContract.activation_date,
                      ).toLocaleDateString('pt-BR')}
                    </S.AsideCardItemValue>
                  </S.AsideCardItem>
                  <S.AsideCardItem>
                    Data Final de contrato:
                    <S.AsideCardItemValue>
                      {parseDateFromISO(
                        currentContract.period_contract_final,
                      ).toLocaleDateString('pt-BR')}
                    </S.AsideCardItemValue>
                  </S.AsideCardItem>
                  {/* <S.AsideCardItem>
                    Nome:
                    <S.AsideCardItemValue>
                      {user?.professional?.status ? 'Ativo' : 'Inativo'}
                    </S.AsideCardItemValue>
                  </S.AsideCardItem>
                  <S.AsideCardItem>
                    Valor:
                    <S.AsideCardItemValue>
                      {user?.professional?.status ? 'Ativo' : 'Inativo'}
                    </S.AsideCardItemValue>
                  </S.AsideCardItem> */}
                  <S.AsideCardItem>
                    Período de Contrato - Início:
                    <S.AsideCardItemValue>
                      {parseDateFromISO(
                        currentContract.period_contract_initial,
                      ).toLocaleDateString('pt-BR')}
                    </S.AsideCardItemValue>
                    {/* <S.PlanStatusValueSpan isActive={user?.professional?.status}>
                      {user?.professional?.contracts[0]?.paymentHistory[0]
                        .status === 'pending'
                        ? 'Pendente'
                        : 'Pago'}
                    </S.PlanStatusValueSpan> */}
                  </S.AsideCardItem>
                  <S.AsideCardItem>
                    Período de Contrato - Fim:
                    <S.AsideCardItemValue>
                      {parseDateFromISO(
                        currentContract.period_contract_final,
                      ).toLocaleDateString('pt-BR')}
                    </S.AsideCardItemValue>
                  </S.AsideCardItem>
                  <S.AsideCardItem>
                    Status
                    <S.AsideCardItemValue style={{ width: '50%' }}>
                      <Form
                        onSubmit={() => {
                          console.log('status');
                        }}
                        style={{ width: '100%' }}
                      >
                        <Select
                          style={{ textAlign: 'left', padding: '0 8px' }}
                          containerStyle={{ marginBottom: '0' }}
                          name="status"
                          value={currentContract.status}
                          // initialValue={contracts[0].status}
                          options={statusOptions}
                          onChange={(event) => {
                            handleChangeStatus(event.target.value);
                          }}
                        />
                      </Form>
                    </S.AsideCardItemValue>
                  </S.AsideCardItem>
                </S.AsideCardInsideContainer>
                <Button
                  variant="black"
                  extraStyles={{ width: '100%' }}
                  textContent="Editar contrato"
                  onClick={() => {
                    if (
                      currentContract?.status === 'finalized' ||
                      currentContract?.status === 'cancelled'
                    ) {
                      setContractEditBlockedModal(true);
                    } else {
                      setContractEditBlockedModal(false);
                    }
                    setContractId(currentContract.id);
                    setContractModal(true);
                  }}
                  size="small"
                />

                <Button
                  variant="black"
                  extraStyles={{ width: '100%' }}
                  textContent="Histórico de pagamento"
                  onClick={() => {
                    router.push({
                      pathname: `/users/details/${currentContract.professional_id}/payments`,
                      state: {
                        currentContractId: currentContract.professional_id,
                      },
                    });
                  }}
                  size="small"
                />
              </>
            )}
            <Button
              variant="border"
              extraStyles={{ width: '100%' }}
              textContent="Novo contrato"
              onClick={() => {
                setContractId(-1);
                setContractModal(true);
              }}
              size="small"
            />
          </S.AsideCard>
          <S.AsideCard>
            <S.AsideCardTitle>Alunos</S.AsideCardTitle>
            <S.AsideCardInsideContainer containerType="column">
              <S.AsideCardItem>
                Status:
                <S.AsideCardItemValue>
                  {user?.professional?.status ? 'Ativo' : 'Inativo'}
                </S.AsideCardItemValue>
              </S.AsideCardItem>
              {/* <S.Divider /> */}
              {/* <S.AsideCardItem>
                Limite de alunos:
                <S.AsideCardItemValue>
                  {`(${students?.total}/250)`}
                </S.AsideCardItemValue>
              </S.AsideCardItem> */}
              <S.Divider />
              <S.AsideCardItem>
                Alunos ativos:
                <S.AsideCardItemValue>
                  {students?.data?.filter(
                    // (user) => user.student?.plan_status === 'active',
                    (user) => user.student?.plan_status === true,
                  ).length || 0}
                </S.AsideCardItemValue>
              </S.AsideCardItem>
              <S.Divider />
              <S.AsideCardItem>
                Alunos inativos:
                <S.AsideCardItemValue>
                  {students?.data?.filter(
                    // (user) => user.student?.plan_status !== 'active',
                    (user) => user.student?.plan_status !== true,
                  ).length || 0}
                </S.AsideCardItemValue>
              </S.AsideCardItem>
              <S.Divider />
              <S.AsideCardItem>
                Total de alunos:
                <S.AsideCardItemValue>
                  {students?.data?.length || 0}
                </S.AsideCardItemValue>
              </S.AsideCardItem>
              {/* <S.Divider />
              <S.AsideCardItem>
                Limite disponível:
                <S.AsideCardItemValue>20/08/2020</S.AsideCardItemValue>
              </S.AsideCardItem> */}
            </S.AsideCardInsideContainer>
            <Button
              variant="border"
              textContent="Visualizar lista de alunos"
              onClick={() =>
                router.push({
                  pathname: `/users/details/${id}/students`,
                  state: { professionalId: user?.professional?.id },
                })
              }
              size="small"
            />
          </S.AsideCard>
        </S.Aside>
      </S.Container>

      <ConfirmModal
        modalIsVisible={confirmDeleteModal}
        setModalIsVisible={setConfirmDeleteModal}
        handleSubmit={handleDeleteProfessional}
        textContent={`Tem certeza de que deseja excluir permanentemente o usuário ${user?.name}?`}
        title="Deletar usuário"
      />
      <ContractModal
        editForm={contractId !== -1}
        trainerId={Number(id)}
        contractId={contractId}
        blockedEdit={contractEditBlockedModal}
        modalIsVisible={contractModal}
        setModalIsVisible={setContractModal}
      />
    </>
  );
};

export default UserDetails;
